import React from 'react';
import { motion } from 'framer-motion';

// Placeholder for icons, you might want to use a library like react-icons
const ArrowRightIcon = () => (
  <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" className="group-hover:translate-x-1 transition-transform" height="16" width="16" xmlns="http://www.w3.org/2000/svg"><path d="M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z"></path></svg>
);

const CheckCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
);

const ClockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
);

function Hero() {
  // Placeholder for the SVG background pattern
  const backgroundPattern = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1, // Stagger children animations
        delayChildren: 0.2,   // Small delay before starting children
      }
    }
  };

  const textWordVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { type: 'spring', stiffness: 100, damping: 12 }
    },
  };

  const paragraphVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.6, ease: "easeOut", delay: 0.5 } // Delay after heading words
    },
  };

  const buttonContainerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { delay: 0.8, duration: 0.5 } } // Delay after paragraph
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6, delay: 0.2, ease: "easeOut" } },
  };

  const statVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
  };

  const headingText = "Transform Your Business Digitally";
  const words = headingText.split(" ");

  return (
    <section id="home" className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
      {/* Background Gradients & Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-white to-secondary/5 z-0"></div>
      <div className="absolute inset-0 z-0 opacity-10" style={{ backgroundImage: backgroundPattern }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          {/* Left Column: Text Content - Apply container variants */}
          <motion.div
            className="lg:w-1/2"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Animated Heading */}
            <motion.h1 
              className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-6"
              aria-label={headingText}
            >
              {words.map((word, index) => (
                <motion.span key={index} variants={textWordVariants} className="inline-block mr-2 md:mr-3">
                  {/* Apply gradient specifically to the first word */}
                  {index === 0 ? (
                    <span className="bg-gradient-to-r from-primary to-secondary text-transparent bg-clip-text">
                      {word}
                    </span>
                  ) : (
                    word
                  )}
                </motion.span>
              ))}
            </motion.h1>

            {/* Animated Paragraph */}
            <motion.p 
              className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl"
              variants={paragraphVariants}
            >
              Expert services in Salesforce, SAP, and Dynamics 365 integration to unlock your enterprise potential and drive innovation.
            </motion.p>

            {/* Animated Buttons */}
            <motion.div 
              className="flex flex-wrap gap-4"
              variants={buttonContainerVariants}
            >
              <a href="#services" className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition-all hover:shadow-lg flex items-center justify-center gap-2 group" tabIndex="0">
                Explore Services
                <ArrowRightIcon />
              </a>
              <a href="#contact" className="border-2 border-primary text-primary px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-all" tabIndex="0">
                Contact Us
              </a>
            </motion.div>
          </motion.div>

          {/* Right Column: Image */}
          <motion.div
            className="lg:w-1/2"
            initial="hidden"
            animate="visible"
            variants={imageVariants}
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="IT Services Solutions"
                className="rounded-2xl shadow-2xl max-w-full h-auto"
              />
              {/* Animated stat boxes with hover effect */}
              <motion.div
                  className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg flex items-center gap-3 cursor-default" // Added cursor-default
                  variants={statVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.5 }}
                  whileHover={{ scale: 1.05, transition: { duration: 0.2 } }} // Added hover effect
                >
                <div className="bg-blue-100 rounded-full p-2">
                  <CheckCircleIcon />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">99.9%</p>
                  <p className="text-xs text-gray-500">Success Rate</p>
                </div>
              </motion.div>
              <motion.div
                  className="absolute -top-6 -right-6 bg-white p-4 rounded-lg shadow-lg flex items-center gap-3 cursor-default" // Added cursor-default
                  variants={statVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.05, transition: { duration: 0.2 } }} // Added hover effect
                >
                <div className="bg-blue-100 rounded-full p-2">
                  <ClockIcon />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">24/7</p>
                  <p className="text-xs text-gray-500">Support</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Commented out Trusted By Section */}
        {/* 
          <motion.div
            className="mt-16 pt-8 border-t border-gray-200"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <p className="text-sm text-center text-gray-500 mb-6">Trusted by leading companies worldwide</p>
            <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-6 opacity-70">
              <div className="text-xl font-bold text-gray-400">COMPANY 1</div>
              <div className="text-xl font-bold text-gray-400">COMPANY 2</div>
              <div className="text-xl font-bold text-gray-400">COMPANY 3</div>
              <div className="text-xl font-bold text-gray-400">COMPANY 4</div>
              <div className="text-xl font-bold text-gray-400">COMPANY 5</div>
            </div>
          </motion.div>
        */}
      </div>
    </section>
  );
}

export default Hero; 